import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MessageService } from 'primeng/api';

import { AgentBankPaymentReviewApiService } from 'src/app/services/agent-bank-payment-review-api.service';
import { BankPaymentDocumentService, PaymentPersistError, PaymentPreconditionError } from 'src/app/services/bank-payment-document.service';
import { AgentBankPaymentReviewItem } from 'src/app/shared/agent-bank-payment-review';
import { SplitRecoveryEvent } from 'src/app/dialogs/agent-bank-payment-split/agent-bank-payment-split.component';
import { Document } from 'src/app/shared/document';

type ReviewFilter = 'pending' | 'processed' | 'ignored';

/** Open-debt tolerance, same as `invoiceAlreadyPaid()`'s. */
const DEBT_TOLERANCE = 0.01;

/**
 * Bank-payment review MVP dialog. Opened from Home's "Платежи" tab (a new
 * button next to the existing copy-link one), visible to the same
 * finance-manager/portal-admin audience `canSeeFinanceResult` already gates.
 * See `docs/PROMPT_agent-bank-payment-review-mvp.md` for the full spec this
 * implements -- summarized in the design notes below.
 *
 * **Deliberately a separate dialog, not a tab inside `CreateNewDocumentComponent`**
 * (per the spec): this is a routine work-queue over many bank transactions,
 * not the editor for one document. It only opens the existing
 * `CreateNewDocumentComponent` per-row, once a manager has confirmed which
 * invoice a transaction pays -- reusing its normal payment editor/save flow
 * rather than building a second document writer.
 *
 * **One transaction -> one invoice -> one payment, no allocation ledger.**
 * No global "create N documents" button, no split-payment loop, no
 * automatic creation at any confidence tier -- every payment is a manager's
 * explicit "Провести оплату" click against an explicitly confirmed invoice.
 */
@Component({
  selector: 'app-agent-bank-payment-review',
  templateUrl: './agent-bank-payment-review.component.html',
  styleUrls: ['./agent-bank-payment-review.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class AgentBankPaymentReviewComponent implements OnChanges {

  /** Controls dialog visibility; two-way bound via `displayChange`. */
  @Input() display: boolean = false;

  /** Emits false to close the dialog (two-way binding partner for `display`). */
  @Output() displayChange = new EventEmitter<boolean>();

  items: AgentBankPaymentReviewItem[] = [];
  loading: boolean = false;
  filter: ReviewFilter = 'pending';

  /** The row currently being paid, kept only while the nested
   *  `CreateNewDocumentComponent` payment dialog is open. */
  activeRow: AgentBankPaymentReviewItem = null;
  paymentDialogVisible: boolean = false;
  paymentDialogParentDocumentId: number = null;
  paymentDialogSeed: { amountDecimal: number; bookingDate: Date | null } = null;

  /** Set when a payment was genuinely created and saved but recording that
   *  against the review row (`PATCH /:id/process`) failed -- e.g. a network
   *  blip right after the save. Never re-shown as "nothing happened": the
   *  real DOCID stays visible and the only retry offered re-sends the same
   *  status update, never another payment POST. */
  recoveryRow: AgentBankPaymentReviewItem = null;
  recoveryPaymentDocumentId: number = null;
  /** The invoice the saved payment was actually created against, captured when
   *  the recovery starts. `retryRecovery()` must use this, never
   *  `recoveryRow.selectedInvoiceId`: the document selector stays editable
   *  while the recovery banner is up, so the live value can have changed. */
  recoveryInvoiceDocumentId: number = null;

  /** True from the moment an auto-create starts until it has fully settled.
   *  Guards against a double-click creating two payments (checked in code, not
   *  just by disabling the button) and serialises auto-creates panel-wide: the
   *  API assigns new DOCIDs as MAX(DOCID)+1, so two concurrent creations from
   *  one browser could collide. */
  autoInFlight: boolean = false;

  /** The "split across invoices" dialog and the row it was opened for. */
  splitVisible: boolean = false;
  splitRow: AgentBankPaymentReviewItem = null;

  constructor(
    private api: AgentBankPaymentReviewApiService,
    private messageService: MessageService,
    private paymentDocs: BankPaymentDocumentService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.display && this.display) {
      this.filter = 'pending';
      this.recoveryRow = null;
      this.recoveryPaymentDocumentId = null;
      this.recoveryInvoiceDocumentId = null;
      this.loadList();
    }
  }

  /** Emits `false` via `displayChange` to close the dialog. */
  onHideDialog() {
    this.displayChange.emit(false);
  }

  loadList() {
    this.loading = true;
    this.api.getList(this.filter).subscribe({
      next: data => {
        this.items = (data || []).map(row => {
          // Preselect the agent's own suggestion only once it has actually
          // resolved to a real document server-side (matched_document_number
          // is only ever populated when the LEFT JOIN found a live row) --
          // never preselect a matched_document_id whose document has since
          // been deleted.
          const preselected = (row.matched_document_id && row.matched_document_number)
            ? row.matched_document_id
            : null;
          const item: AgentBankPaymentReviewItem = {
            ...row,
            selectedInvoiceId: preselected,
            // The debt-check figures for that same preselected invoice --
            // see onInvoiceSelected() for the manually-picked case.
            selectedInvoiceSum: preselected ? row.matched_document_sum : undefined,
            selectedInvoicePaidSum: preselected ? row.matched_document_paidsum : undefined,
          };
          item.actionItems = [
            {
              label: 'Відкрити для перегляду', icon: 'pi pi-eye',
              command: () => {
                // The main button is disabled without an invoice; this menu item is not (the
                // split entry below must stay reachable), so say why nothing opens.
                if (!item.selectedInvoiceId) {
                  this.messageService.add({ severity: 'info', summary: 'Оберіть рахунок', detail: 'Спочатку оберіть рахунок для цієї транзакції.' });
                  return;
                }
                // Same for a fully paid invoice: onCreatePayment() would return without a word.
                if (this.invoiceAlreadyPaid(item)) {
                  this.messageService.add({ severity: 'info', summary: 'Рахунок уже сплачено', detail: 'Цей рахунок уже повністю сплачено -- ймовірно, транзакція дублює наявну оплату. Оберіть інший рахунок або натисніть «Ігнорувати».' });
                  return;
                }
                this.onCreatePayment(item);
              }
            },
            { label: 'Розділити по рахунках…', icon: 'pi pi-sitemap', command: () => this.onOpenSplit(item) }
          ];
          return item;
        });
        this.loading = false;
      },
      error: err => {
        console.error('*** AgentBankPaymentReviewComponent loadList error:', err);
        this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося завантажити список транзакцій.' });
        this.loading = false;
      }
    });
  }

  onFilterChange(filter: ReviewFilter) {
    this.filter = filter;
    this.loadList();
  }

  /** `app-documentselector`'s `(selectedDocument)` handler -- the manager's
   *  explicit choice/confirmation of which invoice this row pays. Overwrites
   *  the agent's own suggestion when the manager picks something else.
   *  `doc` is the live `Document` (this component only emits on a genuine
   *  user pick), so its own `DOCSUM`/`DOCPAIDSUM` replace whatever the
   *  agent's suggestion carried -- always the freshest figures available for
   *  the debt check. */
  onInvoiceSelected(row: AgentBankPaymentReviewItem, doc: Document) {
    row.selectedInvoiceId = doc ? doc.DOCID : null;
    row.selectedInvoiceSum = doc ? doc.DOCSUM : undefined;
    row.selectedInvoicePaidSum = doc ? doc.DOCPAIDSUM : undefined;
  }

  /** True only when both figures for the currently-confirmed invoice are
   *  actually known and show it fully paid already -- i.e. someone settled
   *  it outside this flow, and this bank transaction is very likely a
   *  duplicate a manager should Ignore rather than pay again. Either figure
   *  missing means "unknown", which must never block "Провести оплату" --
   *  a false "already paid" is worse than asking the manager to look. */
  invoiceAlreadyPaid(row: AgentBankPaymentReviewItem): boolean {
    if (row.selectedInvoiceSum == null || row.selectedInvoicePaidSum == null) {
      return false;
    }
    return row.selectedInvoicePaidSum >= row.selectedInvoiceSum - 0.01;
  }

  onIgnore(row: AgentBankPaymentReviewItem) {
    // Ignoring while an auto-create is mid-flight could move the row out of
    // `pending` before /process records the new payment against it.
    if (this.autoInFlight) {
      return;
    }
    this.api.ignoreTransaction(row.id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Готово', detail: 'Транзакцію проігноровано.' });
        this.loadList();
      },
      error: err => {
        console.error('*** AgentBankPaymentReviewComponent onIgnore error:', err);
        this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося проігнорувати транзакцію.' });
      }
    });
  }

  onReopen(row: AgentBankPaymentReviewItem) {
    this.api.reopenTransaction(row.id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Готово', detail: 'Транзакцію повернено в чергу.' });
        this.loadList();
      },
      error: err => {
        console.error('*** AgentBankPaymentReviewComponent onReopen error:', err);
        this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося повернути транзакцію в чергу.' });
      }
    });
  }

  /** Why the one-click action cannot run for this row right now, or `null` when
   *  every pre-click condition holds. A non-null reason never blocks the
   *  button: the main click then opens the review dialog instead, and the
   *  reason is its tooltip. (Currency is checked after the click, once the
   *  invoice is loaded -- see `onAutoCreate()`.) "Invoice confirmed" and
   *  "not already fully paid" are handled by the button being disabled. */
  autoIneligibleReason(row: AgentBankPaymentReviewItem): string | null {
    if (this.recoveryRow) {
      return 'Спочатку завершіть фіксацію попереднього платежу';
    }
    if (!(row.amount_decimal > 0)) {
      return 'Некоректна сума транзакції';
    }
    if (row.selectedInvoiceSum == null || row.selectedInvoicePaidSum == null) {
      return 'Невідомий залишок боргу за рахунком';
    }
    if (row.amount_decimal > row.selectedInvoiceSum - row.selectedInvoicePaidSum + DEBT_TOLERANCE) {
      return 'Сума перевищує борг за рахунком';
    }
    if (!row.booking_date || isNaN(new Date(row.booking_date).getTime())) {
      return 'Не визначено дату платежу';
    }
    return null;
  }

  /** Tooltip of the split button's main action. */
  autoTooltip(row: AgentBankPaymentReviewItem): string {
    if (!row.selectedInvoiceId) {
      return 'Спочатку оберіть рахунок';
    }
    if (this.invoiceAlreadyPaid(row)) {
      return 'Рахунок вже оплачено';
    }
    const reason = this.autoIneligibleReason(row);
    return reason ? reason + ' -- відкриється вікно перегляду' : 'Створити платіж без відкриття вікна';
  }

  /** The split button's main click: create automatically when every condition
   *  holds, otherwise fall back to the review dialog. Never creates silently
   *  in a doubtful case. */
  onPrimaryAction(row: AgentBankPaymentReviewItem) {
    if (this.autoInFlight || !row.selectedInvoiceId || this.invoiceAlreadyPaid(row)) {
      return;
    }
    if (this.autoIneligibleReason(row)) {
      this.onCreatePayment(row);
      return;
    }
    this.onAutoCreate(row);
  }

  /** One-click path: loads the real invoice, re-checks the conditions against
   *  its fresh figures and currency, builds the same payment the review dialog
   *  would (`BankPaymentDocumentService`), saves it once without retry, then
   *  records the row as processed. Only the row that was acted on is removed
   *  from the list, so other rows keep their manually picked invoices. */
  private onAutoCreate(row: AgentBankPaymentReviewItem) {
    this.autoInFlight = true;
    const invoiceId = row.selectedInvoiceId;

    this.paymentDocs.createPaymentForInvoice({
      invoiceId,
      amount: row.amount_decimal,
      bankCurrencyCode: row.currency_code,
      bookingDate: row.booking_date,
    }).subscribe({
      next: paymentId => {
        this.api.processTransaction(row.id, paymentId, invoiceId).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Готово', detail: 'Платіж №' + paymentId + ' збережено.' });
            this.items = this.items.filter(item => item.id !== row.id);
            this.autoInFlight = false;
          },
          error: err => {
            // The payment exists; only recording it against the row failed.
            // Same recovery as the review path: retry that status update only.
            console.error('*** AgentBankPaymentReviewComponent onAutoCreate: process() failed after a real save:', err);
            this.recoveryRow = row;
            this.recoveryPaymentDocumentId = paymentId;
            this.recoveryInvoiceDocumentId = invoiceId;
            this.autoInFlight = false;
          }
        });
      },
      error: err => {
        this.autoInFlight = false;
        if (err instanceof PaymentPreconditionError) {
          this.messageService.add({ severity: 'warn', summary: 'Відкрито для перегляду', detail: err.message });
          this.onCreatePayment(row);
        } else if (err instanceof PaymentPersistError && err.stage === 'items') {
          console.error('*** AgentBankPaymentReviewComponent onAutoCreate: header saved, item failed:', err);
          this.messageService.add({
            severity: 'error', summary: 'Помилка', sticky: true,
            detail: 'Створено платіж №' + err.documentId + ' без рядка платежу. Транзакцію не зафіксовано -- перевірте цей документ у списку документів.'
          });
        } else if (err instanceof PaymentPersistError) {
          console.error('*** AgentBankPaymentReviewComponent onAutoCreate: header save failed or unknown:', err);
          this.messageService.add({
            severity: 'error', summary: 'Помилка', sticky: true,
            detail: 'Не вдалося підтвердити створення платежу. Перевірте список документів, перш ніж повторювати.'
          });
        } else {
          console.error('*** AgentBankPaymentReviewComponent onAutoCreate error:', err);
          this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося підготувати платіж. Платіж не створено.' });
        }
      }
    });
  }

  /** Opens the existing `CreateNewDocumentComponent` payment editor, seeded
   *  from this row's confirmed invoice/amount/date. Requires an explicitly
   *  confirmed `selectedInvoiceId` -- a no-match row uses Ignore instead. */
  onCreatePayment(row: AgentBankPaymentReviewItem) {
    if (!row.selectedInvoiceId || this.invoiceAlreadyPaid(row)) {
      return;
    }

    this.activeRow = row;
    this.paymentDialogParentDocumentId = row.selectedInvoiceId;
    this.paymentDialogSeed = {
      amountDecimal: row.amount_decimal,
      // Guard, per this app's own "HttpClient never revives dates" rule
      // (CLAUDE.md) -- booking_date arrives as a string at runtime despite
      // its API-side Date type.
      bookingDate: row.booking_date ? this.paymentDocs.bookingDateForPayment(row.booking_date) : null,
    };
    this.paymentDialogVisible = true;
  }

  /** Opens the split-across-invoices dialog for one pending row. */
  onOpenSplit(row: AgentBankPaymentReviewItem) {
    if (this.autoInFlight) {
      return;
    }
    if (this.recoveryRow) {
      this.messageService.add({ severity: 'warn', summary: 'Зачекайте', detail: 'Спочатку завершіть фіксацію попереднього платежу.' });
      return;
    }
    this.splitRow = row;
    this.splitVisible = true;
  }

  onSplitDisplayChange(visible: boolean) {
    this.splitVisible = visible;
    if (!visible) {
      this.splitRow = null;
    }
  }

  /** The dialog recorded the bank row as processed (right after the first payment saved). */
  onSplitRowProcessed(rowId: number) {
    this.items = this.items.filter(item => item.id !== rowId);
  }

  /** The dialog saved a payment but could not record the row: same banner as the other paths. */
  onSplitRecovery(event: SplitRecoveryEvent) {
    this.recoveryRow = event.row;
    this.recoveryPaymentDocumentId = event.paymentDocumentId;
    this.recoveryInvoiceDocumentId = event.invoiceDocumentId;
  }

  onPaymentDialogDisplayChange(visible: boolean) {
    this.paymentDialogVisible = visible;
    if (!visible) {
      this.activeRow = null;
    }
  }

  /** `CreateNewDocumentComponent`'s `(documentSaved)` -- fires only once the
   *  payment's header + items have genuinely persisted. Records the outcome
   *  against this row; never marks it processed on any earlier signal. */
  onPaymentDocumentSaved(event: { documentId: number; doctype: number }) {
    const row = this.activeRow;
    if (!row) {
      return;
    }

    const invoiceId = row.selectedInvoiceId;
    this.api.processTransaction(row.id, event.documentId, invoiceId).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Готово', detail: 'Платіж №' + event.documentId + ' збережено.' });
        this.activeRow = null;
        this.loadList();
      },
      error: err => {
        // The payment document itself already exists and is saved -- only
        // recording that against the review row failed. This is never shown
        // as "nothing happened".
        console.error('*** AgentBankPaymentReviewComponent onPaymentDocumentSaved: process() failed after a real save:', err);
        this.recoveryRow = row;
        this.recoveryPaymentDocumentId = event.documentId;
        this.recoveryInvoiceDocumentId = invoiceId;
        this.activeRow = null;
      }
    });
  }

  /** Retries only the status update against the already-created payment --
   *  never another payment POST. */
  retryRecovery() {
    if (!this.recoveryRow || !this.recoveryPaymentDocumentId || !this.recoveryInvoiceDocumentId) {
      return;
    }
    const row = this.recoveryRow;
    const paymentDocumentId = this.recoveryPaymentDocumentId;
    const invoiceId = this.recoveryInvoiceDocumentId;

    this.api.processTransaction(row.id, paymentDocumentId, invoiceId).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Готово', detail: 'Платіж №' + paymentDocumentId + ' збережено.' });
        this.recoveryRow = null;
        this.recoveryPaymentDocumentId = null;
        this.recoveryInvoiceDocumentId = null;
        this.loadList();
      },
      error: err => {
        console.error('*** AgentBankPaymentReviewComponent retryRecovery error:', err);
        this.messageService.add({
          severity: 'error', summary: 'Помилка',
          detail: 'Повторна спроба не вдалася. Платіж №' + paymentDocumentId + ' вже створено -- перевірте документ вручну.'
        });
      }
    });
  }

  dismissRecovery() {
    this.recoveryRow = null;
    this.recoveryPaymentDocumentId = null;
    this.recoveryInvoiceDocumentId = null;
  }
}
